import React, { lazy, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { getEnvironment } from 'helpers'
import { usePageInfo } from 'providers/PageInfoProvider'

import {
  sendFacebookParamsAction,
  sendGoogleAdsParamsAction,
  setCookieForUSAction,
} from 'root-redux/actions/user'
import {
  selectCurrentVariantCohortToUse,
  selectLanguage,
} from 'root-redux/selects/common'
import {
  selectUUID,
  selectUserCountryCode,
  selectUserState,
} from 'root-redux/selects/user'

import { useAmplitudeInitialization } from 'hooks/analytics/useAmplitudeInitialization'
import { useApplovinPixelInitialization } from 'hooks/analytics/useApplovinPixelInitialization'
import { useGAPageTracker } from 'hooks/analytics/useGAPageTracker'
import { useGiaFlowTrackerInitialization } from 'hooks/analytics/useGiaFlowTrackerInitialization'
import { useBulkSelector } from 'hooks/common/useBulkSelector'
import { useCookieConsentAnswer } from 'hooks/common/useCookieConsentAnswer'
import { useSetPlanAdditions } from 'hooks/common/useSetPlanAdditions'
import { useStartSession } from 'hooks/common/useStartSession'
import { useUtmTags } from 'hooks/common/useUtmTags'
import { growthbook } from 'hooks/services/useGrowthBook'
import { useDynamicDiscount } from 'hooks/ui/useDynamicDiscount'
import { useScrollToTop } from 'hooks/ui/useScrollToTop'

import { googleAnalyticsLogger } from 'services/googleAnalytics.service'

import { CookieConsent } from 'components/CookieConsent'
import { Header } from 'components/Header'
import {
  FamylocateGlobalStyles,
  MainSafetyGlobalStyles,
} from 'components/LayoutProvider/LayoutProvider.styles'
import { Theme } from 'components/Theme'

import famyLocateLogoV2 from 'assets/images/famylocate-header-logo-v2.png'

import {
  COOKIE_CONSENT_LANGUAGES,
  Cohort,
  DEFAULT_HEADER_BORDER,
  FAMYLOCATE_COHORTS,
  GROWTH_BOOK_FEATURE_KEYS,
  Language,
} from 'root-constants/common'

import { RouteList } from '../../RouteList'

const InContextPopup = lazy(() =>
  import('components/InContextPopup').then((m) => ({
    default: m.InContextPopup,
  })),
)

const LanguageSelector = lazy(() =>
  import('components/LanguageSelector').then((m) => ({
    default: m.LanguageSelector,
  })),
)

const useSelectors = () =>
  useBulkSelector({
    cohortToUse: selectCurrentVariantCohortToUse,
    language: selectLanguage,
    uuid: selectUUID,
    userCountryCode: selectUserCountryCode,
    state: selectUserState,
  })

export const LayoutProvider: React.FC = () => {
  const dispatch = useDispatch()
  const { search, pathname } = useLocation()
  const { i18n } = useTranslation()
  const { cohortToUse, language, uuid, userCountryCode, state } = useSelectors()

  const { isStageEnvironment, isTestEnvironment } = getEnvironment()
  const isLanguageSelectorShown = isStageEnvironment || isTestEnvironment

  const { isSecondPage, isSecondUpsellPage, isPostPaymentPage } = usePageInfo()

  const hasCookieForUS = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_KEYS.car_1757_cookie_banner,
  )

  useUtmTags()
  useGAPageTracker()
  useGiaFlowTrackerInitialization()
  useAmplitudeInitialization()
  useApplovinPixelInitialization()
  useStartSession()
  useSetPlanAdditions()
  useScrollToTop()
  useDynamicDiscount()

  const { cookieConsent, isPermissionNeeded } = useCookieConsentAnswer()

  const hasAlternativeStyles = FAMYLOCATE_COHORTS.includes(cohortToUse)
  const hasAlternativeFamyLocateHeader =
    FAMYLOCATE_COHORTS.includes(cohortToUse) && isSecondUpsellPage
  const hasMainSafetyStyles =
    cohortToUse === Cohort.MAIN_SAFETY_V3 && !isPostPaymentPage

  const isCookieConsentAvailable =
    !cookieConsent &&
    isPermissionNeeded &&
    COOKIE_CONSENT_LANGUAGES.includes(language)

  useEffect(() => {
    dispatch(setCookieForUSAction(hasCookieForUS))
  }, [hasCookieForUS, dispatch])

  useEffect(() => {
    i18n.changeLanguage(language)
    document.documentElement.dir = language === Language.AR ? 'rtl' : 'ltr'
  }, [i18n, language])

  useEffect(() => {
    if (isSecondPage) {
      dispatch(sendFacebookParamsAction())
      dispatch(sendGoogleAdsParamsAction())
      window.fbq('track', 'CustomizeProduct', {}, { eventID: uuid })
      googleAnalyticsLogger.logFirstPageCompleted()
    }
  }, [dispatch, isSecondPage, uuid])

  useEffect(() => {
    growthbook.setAttributes({
      ...growthbook.getAttributes(),
      url: `${pathname}${search}`,
      country: userCountryCode,
      language,
      state,
    })
  }, [language, pathname, search, userCountryCode, state])

  return (
    <Theme>
      {hasAlternativeStyles && (
        <FamylocateGlobalStyles isSecondUpsellPage={isSecondUpsellPage} />
      )}
      {hasMainSafetyStyles && <MainSafetyGlobalStyles />}

      {isStageEnvironment && <InContextPopup />}
      {isLanguageSelectorShown && <LanguageSelector />}

      <Header
        headerImg={hasAlternativeFamyLocateHeader ? famyLocateLogoV2 : ''}
        headerBorder={
          hasAlternativeFamyLocateHeader ? DEFAULT_HEADER_BORDER : ''
        }
      />
      {isCookieConsentAvailable && <CookieConsent />}
      <RouteList />
    </Theme>
  )
}
